import type { ScanSource } from 'contexts'

/**
 * WHERE THIS OPERATOR'S SCANS COME FROM — remembered, so the picker is asked once and not again.
 *
 * ## Why this exists at all
 *
 * The source used to be decided FOR the operator by whatever happened to be plugged in: a
 * connected desk reader silently claimed every scan (no modal, no choice, even when the tags were
 * across the room), and no reader produced a modal that offered "connect a reader" as a peer
 * option to the thing they could actually do. Making the choice explicit needs somewhere to put
 * the answer, or the choice is asked again on every session.
 *
 * ## Why per-DEVICE (localStorage) and not per-user
 *
 * Same argument as the sidebar's display mode (`components/navigation/Sidebar/displayMode.ts`):
 * the thing being remembered is a property of the MACHINE, not the person. A desk reader is
 * physically attached to one computer, so a preference that followed a user to a laptop with no
 * reader would arrive already unusable. localStorage is per browser profile, which is the right
 * grain.
 *
 * The wrong-device case is self-correcting anyway and that is load-bearing: `readScanSource` only
 * ever answers what was stored, and the CALLER checks whether that source can deliver right now
 * (see `ScanTargetButton`). A remembered reader on a machine with no reader is simply not usable,
 * so the picker opens instead of committing to it. Nothing here can strand an operator on a
 * source that cannot scan.
 *
 * ## Why not the server
 *
 * It would be one more round trip on a control that has to answer a click immediately, to store a
 * fact that is wrong the moment the operator sits at a different desk. Contrast the theme
 * preference and tour progress, both server-backed because they should follow the person.
 */
export type { ScanSource }

export const SCAN_SOURCE_STORAGE_KEY = 'vera:scan:source'

const isScanSource = (value: unknown): value is ScanSource =>
  value === 'phone' || value === 'reader'

/**
 * The remembered source, or `null` when the operator has never chosen one.
 *
 * `null` is meaningful rather than a default: it is what makes the picker open the FIRST time and
 * only the first time. Defaulting to a source here would reintroduce the exact defect this
 * feature removes — a source chosen for the operator by something other than the operator.
 *
 * Every access is wrapped because localStorage throws outright in Safari private browsing and
 * when site data is blocked. Losing the preference costs one extra click; an exception here would
 * take down the toolbar the button sits in.
 */
export const readScanSource = (): ScanSource | null => {
  if (typeof window === 'undefined') return null
  try {
    const stored = window.localStorage.getItem(SCAN_SOURCE_STORAGE_KEY)
    return isScanSource(stored) ? stored : null
  } catch {
    return null
  }
}

/**
 * Remember a source. Written ONLY from an explicit pick in the picker — never from the fallback
 * path, because inferring a preference from "this is what was plugged in" is the behaviour being
 * fixed, and writing it down would make it permanent.
 */
export const writeScanSource = (source: ScanSource) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SCAN_SOURCE_STORAGE_KEY, source)
  } catch {
    // See above.
  }
}
